import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { User, LogOut } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";

export default function UserMenu() {
  const { user, logout } = useAuth(); // Asegúrate de exponer logout() en tu AuthContext
  const [open, setOpen] = useState(false);
  const btnRef = useRef<HTMLButtonElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);

  // Cerrar al hacer click fuera
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      const t = e.target as Node;
      if (!menuRef.current || !btnRef.current) return;
      if (!menuRef.current.contains(t) && !btnRef.current.contains(t))
        setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  // Cerrar con Escape
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    if (open) document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  return (
    <div className="relative">
      <motion.button
        ref={btnRef}
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        className="w-8 h-8 bg-blue-600 rounded-full grid place-items-center shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.98 }}
      >
        <User className="w-4 h-4 text-white" />
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            ref={menuRef}
            role="menu"
            initial={{ opacity: 0, y: -6, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -6, scale: 0.98 }}
            transition={{ duration: 0.15 }}
            className="absolute right-0 mt-2 w-64 rounded-xl border border-gray-200 bg-white shadow-lg z-50 p-2"
          >
            {/* Cabecera mini perfil */}
            <div className="px-3 py-2 border-b">
              <p className="text-sm font-medium text-gray-900 truncate">
                {user?.fullName ?? "Usuario"}
              </p>
              <p className="text-xs text-gray-500 truncate">
                {user?.email ?? ""}
              </p>
              {user?.role && (
                <span className="mt-1 inline-flex rounded-md bg-gray-100 px-2 py-0.5 text-[10px] font-medium text-gray-600">
                  {user.role}
                </span>
              )}
            </div>

            {/* Opciones */}
            <button
              role="menuitem"
              className="w-full text-left flex items-center gap-2 px-3 py-2 rounded-md hover:bg-gray-50"
              onClick={() => {
                logout?.();
                setOpen(false);
              }}
            >
              <LogOut className="w-4 h-4 text-gray-500" />
              <span className="text-sm text-gray-700">Cerrar sesión</span>
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
